import { Component, DestroyRef, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LayoutService } from '../../../../../../../@vex/services/layout.service';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle, MatDatepickerToggleIcon } from '@angular/material/datepicker';
import dayjs, { Dayjs } from 'dayjs';
import { NgClass } from '@angular/common';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOption } from '@angular/material/core';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS } from '../../../../../../util/material-dayjs-adapter';
import {
	PeriodicityType,
	PhysicalUnit,
	TenantPayment,
	TenantPaymentService,
	TenantPaymentType,
	TenantSelection,
	TenantSelectionService,
	TenantSetting,
	TenantSettingService,
	TenantSettingType,
	TenantShare,
	TenantShareService,
	TenantShareType,
	TenantShareTypeMeta,
	TenantTarif,
	TenantTarifService,
	TenantTarifType,
	TenantUnitReference,
	UserRole,
} from '@eadsysteme/billingclient-v1';
import { SessionService } from '../../../../../../services/session.service';
import { DateValidator } from '../../../../../../services/DateValidator';
import { Observable } from 'rxjs';
import { SnackbarService } from '../../../../../../services/snackbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateUtil } from '../../../../../../util/DateUtil';
import { MatSelect, MatSelectChange, MatSelectTrigger } from '@angular/material/select';
import { TranslatorUtil } from '../../../../../../util/TranslatorUtil';
import { MatTooltip } from '@angular/material/tooltip';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';

@Component({
	selector: 'vex-parameter-dialog',
	imports: [
		MatButton,
		MatDialogActions,
		MatDialogContent,
		MatDialogTitle,
		MatIcon,
		TranslocoDirective,
		MatFormField,
		FormsModule,
		MatInput,
		MatLabel,
		MatDatepicker,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatError,
		ReactiveFormsModule,
		NgClass,
		MatSuffix,
		MatOption,
		MatSelect,
		MatDatepickerToggleIcon,
		MatSelectTrigger,
		MatTooltip,
		MatSlideToggle,
		CdkDragHandle,
		CdkDrag,
	],
	templateUrl: './parameter-dialog.component.html',
	styleUrl: './parameter-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		{
			provide: DateAdapter,
			useClass: DayjsDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: DateUtil.DATE_FORMATS },
	],
})
export class ParameterDialogComponent {
	protected layoutService = inject(LayoutService);
	data = inject(MAT_DIALOG_DATA);
	private readonly dialogRef = inject<MatDialogRef<ParameterDialogComponent>>(MatDialogRef);
	private readonly translocoService = inject(TranslocoService);
	private readonly tenantSelectionService = inject(TenantSelectionService);
	private readonly tenantShareService = inject(TenantShareService);
	private readonly tenantPaymentService = inject(TenantPaymentService);
	private readonly tenantTariffService = inject(TenantTarifService);
	private readonly tenantSettingService = inject(TenantSettingService);
	private readonly sessionService = inject(SessionService);
	private readonly snackbarService = inject(SnackbarService);
	private readonly formBuilder = inject(FormBuilder);

	private readonly destroyRef = inject(DestroyRef);
	private translatorUtil = inject(TranslatorUtil);
	private readonly billingId: string;

	parameterFg: FormGroup;
	fromMinDate = dayjs('1970-01-01');
	toMaxDate = dayjs().add(30, 'years');
	toMinDate: Dayjs;
	today = dayjs().utcOffset(0, true);

	protected isMobile: boolean;
	protected translatedPeriods: { value: PeriodicityType; label: string }[];
	protected translatedShareTypes: { value: TenantShareType; label: string }[];
	protected translatedTariffTypes: { value: TenantTarifType; label: string }[];
	protected translatedPaymentTypes: { value: TenantPaymentType; label: string }[];
	protected translatedSettingTypes: { value: TenantSettingType; label: string }[];

	private readonly tenantShareTypeMeta = TenantShareTypeMeta;
	protected units: WritableSignal<PhysicalUnit[]> = signal(Object.values(PhysicalUnit));
	protected availableUnits: WritableSignal<PhysicalUnit[]> = signal(this.units());
	protected validFromIsUnlimited: WritableSignal<boolean> = signal(false);
	protected validToIsUnlimited: WritableSignal<boolean> = signal(false);
	protected selectedTenantUnitReference: TenantUnitReference;
	protected currentParameter: string;
	availableUserRoles: WritableSignal<UserRole[]> = signal([]);

	private readonly parameterCreators = new Map<string, (data: ParameterData) => Observable<any>>([
		['selection', (data) => this.createSelection(data)],
		['share', (data) => this.createShare(data)],
		['tariff', (data) => this.createTariff(data)],
		['payment', (data) => this.createPayment(data)],
		['setting', (data) => this.createSetting(data)],
	]);

	private readonly parameterUpdaters = new Map<string, (id: string, data: ParameterData) => Observable<any>>([
		['selection', (id, data) => this.updateSelection(id, data)],
		['share', (id, data) => this.updateShare(id, data)],
		['tariff', (id, data) => this.updateTariff(id, data)],
		['payment', (id, data) => this.updatePayment(id, data)],
		['setting', (id, data) => this.updateSetting(id, data)],
	]);

	constructor() {
		const layoutService = this.layoutService;
		const data = this.data;

		this.isMobile = layoutService.isMobile();
		this.billingId = data.billingId;
		this.selectedTenantUnitReference = data.billingInstance;
		this.currentParameter = data.currentParameter;
		this.availableUserRoles.set(data.availableUserRoles);

		this.translatedPeriods = this.translatorUtil.translateAndSort(Object.values(PeriodicityType), 'enum.');
		this.translatedShareTypes = this.translatorUtil.translateAndSort(Object.values(TenantShareType), 'tenantShareType.');
		this.translatedTariffTypes = this.translatorUtil.translateAndSort(Object.values(TenantTarifType), 'enum.');
		this.translatedPaymentTypes = this.translatorUtil.translateAndSort(
			Object.values(TenantPaymentType).filter((t) => t !== 'TENANT_ELECTRICITY'),
			'tenantPaymentType.'
		);
		this.translatedSettingTypes = this.translatorUtil.translateAndSort(Object.values(TenantSettingType), 'billing.ordinaryAllocationType.');

		this.parameterFg = this.formBuilder.group(
			{
				name: [{ value: '', disabled: false }, Validators.required],
				printName: [{ value: '', disabled: false }, null],
				description: [{ value: '', disabled: false }, null],
				validFrom: [{ value: '', disabled: false }, Validators.required],
				validTo: [{ value: '', disabled: false }, Validators.required],
				unit: [
					{
						value: '',
						disabled: false,
					},
					['share'].includes(this.currentParameter) ? Validators.required : null,
				],
				periodicity: [{ value: PeriodicityType.BILLING_PERIOD, disabled: false }, Validators.required],
				type: [
					{
						value: undefined,
						// disabled on settings edit
						disabled: ['setting'].includes(this.currentParameter) && !!data.parameter,
					},
					['share', 'tariff', 'payment', 'setting'].includes(this.currentParameter) ? Validators.required : null,
				],
				validUserRole: [
					{
						value: undefined,
						// disabled on settings edit
						disabled: ['setting'].includes(this.currentParameter) && !!data.parameter,
					},
					['setting'].includes(this.currentParameter) ? Validators.required : null,
				],
			},
			{
				validators: [
					DateValidator.dateRangeValidator(() => this.validFromIsUnlimited()),
					DateValidator.dateRangeValidator(() => this.validToIsUnlimited()),
				],
			}
		);

		if (data.parameter) {
			this.parameterFg.patchValue(data.parameter);
			this.parameterFg.get('validUserRole').setValue(this.availableUserRoles().find((aUR) => aUR.exportId === data.parameter.validUserRole));

			if (!data.parameter.validFrom) {
				this.unlimitDate({ checked: true } as MatSlideToggleChange, 'validFrom');
			}
			if (!data.parameter.validTo) {
				this.unlimitDate({ checked: true } as MatSlideToggleChange, 'validTo');
			}
		}

		this.parameterFg.get('validFrom').valueChanges.subscribe((value) => {
			this.toMinDate = dayjs(value)?.add(1, 'days');
		});
	}

	onTypeChange($event: MatSelectChange) {
		if (this.currentParameter === 'share') {
			const selectedShareType = $event.value as TenantShareType;
			const currentUnit = this.tenantShareTypeMeta[selectedShareType].unit;
			this.availableUnits.set([PhysicalUnit[currentUnit]]);
			if (this.availableUnits().length === 1) {
				this.parameterFg.get('unit').setValue(PhysicalUnit[currentUnit] === PhysicalUnit.UNDEFINED ? ' ' : PhysicalUnit[currentUnit]);
				this.parameterFg.get('unit').disable();
			}
		}
	}

	close(shouldSave: boolean) {
		if (!shouldSave) {
			this.dialogRef.close(shouldSave);
			return;
		}
		const parameterData = this.createParameterData();
		const saveOperation = this.getParameterSaveOperation(parameterData);

		if (!saveOperation) {
			console.error(`Unknown parameter type: ${this.currentParameter}`);
			return;
		}
		this.executeSaveOperation(saveOperation);
	}

	private getParameterSaveOperation(data: ParameterData): Observable<any> | null {
		const isEdit = !!this.data.parameter;
		if (isEdit) {
			const updater = this.parameterUpdaters.get(this.currentParameter);
			return updater ? updater(this.data.parameter.id, data) : null;
		} else {
			const creator = this.parameterCreators.get(this.currentParameter);
			return creator ? creator(data) : null;
		}
	}

	private executeSaveOperation(saveOperation: Observable<any>): void {
		saveOperation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: () => this.handleSaveSuccess(),
			error: (error) => this.handleSaveError(error),
		});
	}

	private handleSaveSuccess(): void {
		this.dialogRef.close(true);
		this.snackbarService.success(this.translocoService.translate('billing.saveParameterSuccess'));
	}

	private handleSaveError(error: any): void {
		this.snackbarService.error(this.translocoService.translate('billing.saveParameterError'));
	}

	private createParameterData(): ParameterData {
		return {
			id: this.data.parameter?.id,
			name: this.parameterFg.get('name').value,
			description: this.parameterFg.get('description').value,
			printname: this.parameterFg.get('printName').value,
			validFrom: this.parameterFg.get('validFrom').value,
			validTo: this.parameterFg.get('validTo').value,
			periodicity: this.parameterFg.get('periodicity')?.value ?? null,
			type: this.parameterFg.get('type')?.value ?? null,
			unit: this.parameterFg.get('unit')?.value ?? null,
			validUserRole: this.parameterFg.get('validUserRole')?.value?.id ?? null,
		};
	}

	private createSelection(newParameter: any) {
		return this.tenantSelectionService.createTenantSelection(this.sessionService.effectiveArea, this.billingId, newParameter as TenantSelection);
	}

	private createShare(newParameter: any) {
		return this.tenantShareService.createTenantShare(this.sessionService.effectiveArea, this.billingId, newParameter as TenantShare);
	}

	private createTariff(newParameter: any) {
		return this.tenantTariffService.createTenantTarif(this.sessionService.effectiveArea, this.billingId, newParameter as TenantTarif);
	}

	private createPayment(newParameter: any) {
		return this.tenantPaymentService.createTenantPayment(this.sessionService.effectiveArea, this.billingId, newParameter as TenantPayment);
	}

	private createSetting(newParameter: any) {
		return this.tenantSettingService.createTenantSetting(this.sessionService.effectiveArea, this.billingId, newParameter as TenantSetting);
	}

	private updateSelection(id: string, newParameter: any) {
		return this.tenantSelectionService.updateTenantSelection(this.sessionService.effectiveArea, this.billingId, id, newParameter as TenantSelection);
	}

	private updateShare(id: string, newParameter: any) {
		return this.tenantShareService.updateTenantShare(this.sessionService.effectiveArea, this.billingId, id, newParameter as TenantShare);
	}

	private updateTariff(id: string, newParameter: any) {
		return this.tenantTariffService.updateTenantTarif(this.sessionService.effectiveArea, this.billingId, id, newParameter as TenantTarif);
	}

	private updatePayment(id: string, newParameter: any) {
		return this.tenantPaymentService.updateTenantPayment(this.sessionService.effectiveArea, this.billingId, id, newParameter as TenantPayment);
	}

	private updateSetting(id: string, newParameter: any) {
		return this.tenantSettingService.updateTenantSetting(this.sessionService.effectiveArea, this.billingId, id, newParameter as TenantSetting);
	}

	quicksetDateFrom(formControlName: string, date: Date) {
		this.parameterFg.get(formControlName).setValue(date);
		this.parameterFg.get(formControlName).markAllAsTouched();
	}

	unlimitDate($event: MatSlideToggleChange, formControlName: string) {
		if ($event.checked) {
			this.parameterFg.get(formControlName).disable();
			this.parameterFg.removeValidators(Validators.required);
		} else {
			this.parameterFg.get(formControlName).enable();
			this.parameterFg.addValidators(Validators.required);
		}
		this.parameterFg.get(formControlName).setValue(undefined);

		if (formControlName === 'validFrom') {
			this.validFromIsUnlimited.set(!this.validFromIsUnlimited());
		}
		if (formControlName === 'validTo') {
			this.validToIsUnlimited.set(!this.validToIsUnlimited());
		}
	}
}

interface ParameterData {
	id?: string;
	name: string;
	description: string;
	printname: string;
	validFrom: Date;
	validTo: Date;
	periodicity?: PeriodicityType;
	type?: string;
	unit?: PhysicalUnit;
	validUserRole?: string;
}
